import {
  CalendarOutlined,
  LeftOutlined,
  RightOutlined,
} from "@ant-design/icons"
import { Button, DatePicker, Tooltip } from "antd"
import dayjs from "dayjs"
import { useEffect, useState } from "react"

import { Dayjs } from "#customTypes/types"
import {
  getNow,
  isToday,
  SPELLED_OUT_DATE_FORMAT,
  STANDARD_TIME_FORMAT,
} from "#utils/dateHelpers"

import Flex from "./Flex"

const DateDisplay = ({
  selectedDate,
  setSelectedDate,
}: {
  selectedDate: Dayjs
  setSelectedDate: (date: Dayjs) => void
}) => {
  const [currentTime, setCurrentTime] = useState<Dayjs | null>(null)
  const [isOpen, setIsOpen] = useState(false)

  // Update current time every minute, but force update on mount to align with minute boundaries
  useEffect(() => {
    const now = getNow()

    // Don't show current time if not viewing today
    if (!isToday(selectedDate, now)) {
      setCurrentTime(null)
      return
    }

    setCurrentTime(now)

    // Calculate milliseconds until next minute boundary
    const secondsUntilNextMinute = 60 - now.second()
    const msUntilNextMinute = secondsUntilNextMinute * 1000 + 100

    // Set initial timer to align with minute boundaries
    const initialTimer = setTimeout(() => {
      setCurrentTime(getNow())

      // Then set up regular minute interval
      const regularTimer = setInterval(() => {
        setCurrentTime(getNow())
      }, 60000)

      return () => clearInterval(regularTimer)
    }, msUntilNextMinute)

    return () => clearTimeout(initialTimer)
  }, [selectedDate])

  return (
    <Flex gap={12} center>
      {/* Previous day */}
      <Button
        type="text"
        size="small"
        shape="circle"
        onClick={() => setSelectedDate(selectedDate.subtract(1, "day"))}
        icon={
          <Tooltip title="Previous day">
            <LeftOutlined />
          </Tooltip>
        }
      />

      {/* Selected date */}
      <Flex col center style={{ width: 205 }}>
        <h2>{selectedDate.format(SPELLED_OUT_DATE_FORMAT)}</h2>
        {/* Set height so layout doesn't shift when changing days */}
        <h3 style={{ height: 22 }}>
          {currentTime?.format(STANDARD_TIME_FORMAT)}
        </h3>
      </Flex>

      {/* Today */}
      <Flex style={{ position: "relative" }}>
        <Button
          onClick={() => {
            if (!isOpen) setIsOpen(true)
          }}
          size="small"
        >
          <CalendarOutlined />
        </Button>
        <DatePicker
          value={selectedDate}
          onChange={(date) => {
            setSelectedDate(date)
            setIsOpen(false)
          }}
          onOpenChange={setIsOpen}
          format="M/D/YY"
          style={{
            width: 100,
            position: "absolute",
            top: 0,
            left: 0,
            visibility: "hidden",
          }}
          minDate={dayjs("1/1/2016")}
          open={isOpen}
        />
      </Flex>

      {/* Next day */}
      <Button
        type="text"
        size="small"
        shape="circle"
        onClick={() => setSelectedDate(selectedDate.add(1, "day"))}
        icon={
          <Tooltip title="Next day">
            <RightOutlined />
          </Tooltip>
        }
      />
    </Flex>
  )
}

export default DateDisplay
